import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaReceipt, FaStar, FaClipboardList, FaExternalLinkAlt } from 'react-icons/fa';
import { ClientRestaurant } from '../../types';
import { Receipt } from '../../../../../types/clientReceipts';
import { getReceiptsSummaryByLocalIdAndClientId } from '../../../../../crud/clients_api/client_receipts';
import MembershipProgress from '../Home/components/MembershipProgress/MembershipProgress';
import ReceiptCard from './components/ReceiptCard/ReceiptCard';
import ReviewCard from './components/ReviewCard/ReviewCard';
import PendingCard from './components/PendingCard/PendingCard';
import LoadingSpinner from '../../common/LoadingSpinner';
import { formatCurrency, formatDate } from '../../../../../utils/formatters';
import './RestaurantDetails.scss';

interface RestaurantDetailsProps {
    restaurant: ClientRestaurant;
    onBack: () => void;
}

type TabType = 'receipts' | 'reviews' | 'pending';

const RestaurantDetails: React.FC<RestaurantDetailsProps> = ({ restaurant, onBack }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabType>('receipts');
    const [receipts, setReceipts] = useState<Receipt[]>([]);
    const [loading, setLoading] = useState(true);

    // Get client ID from localStorage
    const getClientId = (): number | null => {
        const clientData = localStorage.getItem('client');
        if (clientData) {
            try {
                const client = JSON.parse(clientData);
                return client.client_id || null;
            } catch {
                return null;
            }
        }
        return null;
    };

    // Fetch receipts on component mount
    useEffect(() => {
        const fetchReceipts = async () => {
            const clientId = getClientId();
            if (!clientId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await getReceiptsSummaryByLocalIdAndClientId(
                    restaurant.local_id,
                    clientId
                );
                
                // API returns { data: Receipt[] }
                setReceipts(response.data || []);
            } catch (error) {
                console.error('Error fetching receipts:', error);
                setReceipts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchReceipts();
    }, [restaurant.local_id]);

    // Filter receipts by review status
    const receiptsWithReviews = receipts.filter(r => r.review !== null);
    const receiptsPendingReview = receipts.filter(r => r.review === null);

    const handleGoToRestaurantPage = () => {
        const url = `https://mediopunto.cl/Clients/Local/${restaurant.local_id}`;
        window.open(url, '_blank');
    };

    const handleViewReview = (receipt: Receipt) => {
        // Navigate to review details or edit page
        // You can customize this navigation based on your routing structure
        console.log('View review for receipt:', receipt.receipt_id);
        // navigate(`/portal/reviews/${receipt.receipt_id}`);
    };

    const handleLeaveReview = (receipt: Receipt) => {
        // Navigate to create review page with redirect parameter
        navigate(`/ClientReviews/${receipt.receipt_id}?redirect=/ClientPortal`);
    };

    const formatCLP = (amount: number): string => {
        return formatCurrency(amount);
    };

    return (
        <div className="restaurant-details">
            {/* Header */}
            <div className="details-header">
                <button className="back-button" onClick={onBack}>
                    <FaArrowLeft />
                </button>
                <h1 className="restaurant-title">{restaurant.local_name}</h1>
            </div>

            {/* Restaurant Summary Card */}
            <div className="restaurant-summary">
                <div className="summary-badges">
                    <span className="membership-badge-detail">{restaurant.membership_name}</span>
                    <span className="discount-badge-detail">-{restaurant.membership_discount}%</span>
                </div>

                <div className="summary-stats">
                    <div className="stat-item">
                        <span className="stat-label">Visitas</span>
                        <span className="stat-value">{restaurant.visits}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Ahorrado</span>
                        <span className="stat-value stat-highlight">{formatCLP(restaurant.total_saved)}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Última Visita</span>
                        <span className="stat-value">{formatDate(restaurant.last_visit)}</span>
                    </div>
                </div>

                {restaurant.next_level_name && restaurant.next_level_required_spent && (
                    <div className="summary-progress">
                        <MembershipProgress
                            currentLevel={restaurant.membership_name}
                            nextLevelName={restaurant.next_level_name}
                            totalSpent={restaurant.total_spent}
                            nextLevelRequiredSpent={restaurant.next_level_required_spent}
                        />
                    </div>
                )}

                <button className="go-to-page-btn" onClick={handleGoToRestaurantPage}>
                    <FaExternalLinkAlt />
                    <span>Ir a su página</span>
                </button>
            </div>

            {/* Tabs */}
            <div className="details-tabs">
                <button 
                    className={`tab-button ${activeTab === 'receipts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('receipts')}
                >
                    <FaReceipt />
                    <span>Recibos</span>
                    <span className="tab-count">{receipts.length}</span>
                </button>
                <button 
                    className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
                    onClick={() => setActiveTab('reviews')}
                >
                    <FaStar />
                    <span>Reseñas</span>
                    <span className="tab-count">{receiptsWithReviews.length}</span>
                </button>
                <button 
                    className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    <FaClipboardList />
                    <span>Pendientes</span>
                    {receiptsPendingReview.length > 0 && (
                        <span className="tab-count pending">{receiptsPendingReview.length}</span>
                    )}
                </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {/* Show loading spinner while fetching */}
                {loading ? (
                    <LoadingSpinner size="large" text="Cargando tus recibos..." />
                ) : (
                    <>
                        {/* Receipts Tab */}
                        {activeTab === 'receipts' && (
                            <div className="receipts-list">
                                {receipts.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon">🧾</div>
                                        <h3>No hay recibos aún</h3>
                                        <p>Tus recibos de este restaurante aparecerán aquí cuando realices una compra</p>
                                    </div>
                                ) : (
                                    receipts.map((receipt) => (
                                        <ReceiptCard
                                            key={receipt.receipt_id}
                                            receipt={receipt}
                                            onViewReview={receipt.review ? handleViewReview : undefined}
                                            onLeaveReview={!receipt.review ? handleLeaveReview : undefined}
                                        />
                                    ))
                                )}
                            </div>
                        )}

                        {/* Reviews Tab */}
                        {activeTab === 'reviews' && (
                            <div className="reviews-list">
                                {receiptsWithReviews.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon">⭐</div>
                                        <h3>No has dejado reseñas aún</h3>
                                        <p>Cuando dejes una reseña para este restaurante, aparecerá aquí</p>
                                    </div>
                                ) : (
                                    receiptsWithReviews.map((receipt) => (
                                        <ReviewCard
                                            key={receipt.receipt_id}
                                            receipt={receipt}
                                        />
                                    ))
                                )}
                            </div>
                        )}

                        {/* Pending Reviews Tab */}
                        {activeTab === 'pending' && (
                            <div className="pending-list">
                                {receiptsPendingReview.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon">✅</div>
                                        <h3>¡Todo al día!</h3>
                                        <p>No tienes reseñas pendientes para este restaurante</p>
                                    </div>
                                ) : (
                                    receiptsPendingReview.map((receipt) => (
                                        <PendingCard
                                            key={receipt.receipt_id}
                                            receipt={receipt}
                                            onLeaveReview={handleLeaveReview}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default RestaurantDetails;
